import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import LightButton from "components/shared/LightButton";
import { formatNumber } from "helpers/utils";
import { useTranslation } from "react-i18next";

import AdvanceSlicesRecap from "../AdvanceSlicesRecap";

interface IAdvanceTotalBlock {
    from: "form" | "drawer";
    mode: "new" | "edit";
    createAdvanceInManagement?: boolean;
    usesInitialAdvance: boolean;
    inlineAdvancePayment?: TAdvancePayment | null;
    inlineAdvanceTotal: number | null;
    drawerAdvanceSlices: TAdvancePayment["slices"] | null;
    drawerAdvanceTotal: number | null;
    onGoToAdvancePayment: () => void;
}

/**
 * Displays the advance payment total line + slices recap table.
 * Resolves whether to use form-mode or drawer-mode data internally.
 * Renders nothing when no advance data is available.
 */
const AdvanceTotalBlock = ({
    from,
    mode,
    createAdvanceInManagement,
    usesInitialAdvance,
    inlineAdvancePayment,
    inlineAdvanceTotal,
    drawerAdvanceSlices,
    drawerAdvanceTotal,
    onGoToAdvancePayment,
}: IAdvanceTotalBlock) => {
    const [t] = useTranslation();

    const isFormMode = createAdvanceInManagement && inlineAdvancePayment?.slices && inlineAdvanceTotal !== null;
    const isDrawerMode = from === "drawer" && drawerAdvanceSlices && drawerAdvanceTotal !== null;

    if (!isFormMode && !isDrawerMode) return null;

    const total = isFormMode ? inlineAdvanceTotal! : drawerAdvanceTotal!;
    const slices = isFormMode ? inlineAdvancePayment!.slices : drawerAdvanceSlices!;
    const showButton = !usesInitialAdvance && (isDrawerMode || mode !== "new");

    return (
        <>
            <AdvanceSlicesRecap slices={slices} />
            <div className={`advance-total-row${isDrawerMode ? " from-drawer" : ""}`}>
                <span className="grey">
                    {t("TermRecap.AdvanceSlicesTitle")}{" "}
                    <span className="option-text">{formatNumber(total, { isCurrency: true, always2Digit: true })}</span>
                </span>
                {showButton && <LightButton text={t("TermRecap.GoToAdvancePayment")} onClick={onGoToAdvancePayment} />}
            </div>
        </>
    );
};

export default AdvanceTotalBlock;
